import axios from 'axios';
import { getApiErrorMessage } from './apiService';

const API_BASE_URL = process.env.REACT_APP_API_URL || '/api';

// Create axios instance with same config as apiService
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true, // Enable cookies for authentication
});

export interface MunkaszamOsszesitoDto {
  munkaszam: string;
  munkaTargya: string;
  osszesOraszam: number;
  utolsoIdoraforditasDatum: string;
  utolsoComment: string;
}

export interface MunkaszamResztvevoOsszesitoDto {
  userId: string;
  felhasznaloNev: string;
  osszesOraszam: number;
  utolsoIdoraforditasDatum: string;
  utolsoComment: string;
}

export interface IdoraforditasDto {
  id: number;
  datum: string;
  bejegyzesTargya: string;
  idoraforditasErteke: number;
  munkaszamId: number;
  userId: string;
  munkaszamErteke: string;
  felhasznaloNev: string;
  workLocation?: string; // 'home_office' | 'office' | 'offsite'
  workType?: string; // Munka típusa (bármely helyszínhez megadható)
}

export type WorkLocationType = 'home_office' | 'office' | 'offsite';

export const WORK_LOCATION_LABELS: Record<WorkLocationType, string> = {
  home_office: 'Home office',
  office: 'Iroda',
  offsite: 'Irodán kívül',
};

// Munka típusok helyszín szerint
export const WORK_TYPES_BY_LOCATION: Record<WorkLocationType, readonly string[]> = {
  office: [
    'Értekezlet',
    'Tervezés',
    'Dokumentálás',
    'Adminisztráció',
  ] as const,
  home_office: [
    'Tervezés',
    'Dokumentálás',
    'Számítás',
    'Kutatás',
  ] as const,
  offsite: [
    'Felmérés',
    'Tervezői művezetés',
    'Műszaki ellenőrzés',
    'Helyszíni bejárás',
  ] as const,
};

export interface TopWorkerDto {
  userId: string;
  userName: string;
  totalHours: number;
  rank: number;
}

export interface DateRangeRequestOptions {
  userId?: string;
  includeAllUsers?: boolean;
}

class IdoraforditasService {
  private toReadableError(error: any, fallbackMessage: string): Error {
    return new Error(getApiErrorMessage(error, fallbackMessage));
  }

  async getMunkaszamokByUserId(userId: string): Promise<MunkaszamOsszesitoDto[]> {
    try {
      const response = await api.get<MunkaszamOsszesitoDto[]>(
        `/idoraforditasok/user/${userId}/munkaszamok`
      );
      return response.data;
    } catch (error) {
      console.error('Hiba a munkaszámok lekérdezése során:', error);
      throw this.toReadableError(error, 'Hiba a munkaszámok lekérdezése során.');
    }
  }

  async getUtolsoMunkaszamokByUserId(userId: string, count: number = 5): Promise<MunkaszamOsszesitoDto[]> {
    try {
      const response = await api.get<MunkaszamOsszesitoDto[]>(
        `/idoraforditasok/user/${userId}/utolso-munkaszamok?count=${count}`
      );
      return response.data;
    } catch (error) {
      console.error('Hiba az utolsó munkaszámok lekérdezése során:', error);
      throw this.toReadableError(error, 'Hiba az utolsó munkaszámok lekérdezése során.');
    }
  }

  async getMindenMunkaszamOsszesito(): Promise<MunkaszamOsszesitoDto[]> {
    try {
      const response = await api.get<MunkaszamOsszesitoDto[]>(
        '/idoraforditasok/munkaszamok/osszes'
      );
      return response.data;
    } catch (error) {
      console.error('Hiba az összes munkaszám összesítő lekérdezése során:', error);
      throw this.toReadableError(error, 'Hiba az összes munkaszám összesítő lekérdezése során.');
    }
  }

  async getIdoraforditasokByUserIdAndMunkaszam(
    userId: string, 
    munkaszam: string
  ): Promise<IdoraforditasDto[]> {
    try {
      const response = await api.get<IdoraforditasDto[]>(
        `/idoraforditasok/user/${userId}/munkaszam/${encodeURIComponent(munkaszam)}`
      );
      return response.data;
    } catch (error) {
      console.error('Hiba az időráfordítások lekérdezése során:', error);
      throw this.toReadableError(error, 'Hiba az időráfordítások lekérdezése során.');
    }
  }

  async getIdoraforditasokByMunkaszam(munkaszam: string): Promise<IdoraforditasDto[]> {
    try {
      const response = await api.get<IdoraforditasDto[]>(
        `/idoraforditasok/munkaszam/${encodeURIComponent(munkaszam)}`
      );
      return response.data;
    } catch (error) {
      console.error('Hiba az időráfordítások lekérdezése során:', error);
      throw this.toReadableError(error, 'Hiba az időráfordítások lekérdezése során.');
    }
  }

  async getIdoraforditasokByParticipantAndMunkaszam(participantName: string, munkaszam: string): Promise<IdoraforditasDto[]> {
    try {
      const response = await api.get<IdoraforditasDto[]>(
        `/idoraforditasok/munkaszam/${encodeURIComponent(munkaszam)}/participant/${encodeURIComponent(participantName)}`
      );
      return response.data;
    } catch (error) {
      console.error('Hiba az időráfordítások lekérdezése során résztvevő és munkaszám alapján:', error);
      throw this.toReadableError(error, 'Hiba az időráfordítások lekérdezése során.');
    }
  }

  async getMunkaszamResztvevokByMunkaszam(munkaszam: string): Promise<MunkaszamResztvevoOsszesitoDto[]> {
    try {
      const response = await api.get<MunkaszamResztvevoOsszesitoDto[]>(
        `/idoraforditasok/munkaszam/${encodeURIComponent(munkaszam)}/resztvevok`
      );
      return response.data;
    } catch (error) {
      console.error('Hiba a munkaszám résztvevőinek lekérdezése során:', error);
      throw this.toReadableError(error, 'Hiba a munkaszám résztvevőinek lekérdezése során.');
    }
  }

  async getIdoraforditasokByDateRange(
    startDate: string,
    endDate: string,
    options?: DateRangeRequestOptions
  ): Promise<IdoraforditasDto[]> {
    try {
      const params = new URLSearchParams({
        startDate,
        endDate,
      });

      if (options?.userId) {
        params.append('userId', options.userId);
      }

      if (options?.includeAllUsers) {
        params.append('includeAllUsers', 'true');
      }

      const response = await api.get<IdoraforditasDto[]>(
        `/idoraforditasok/date-range?${params.toString()}`
      );
      return response.data;
    } catch (error) {
      console.error('Hiba az időráfordítások dátumtartomány szerinti lekérdezése során:', error);
      throw this.toReadableError(error, 'Hiba az időráfordítások dátumtartomány szerinti lekérdezése során.');
    }
  }

  async createIdoraforditas(data: Partial<IdoraforditasDto>): Promise<IdoraforditasDto> {
    try {
      const response = await api.post<IdoraforditasDto>('/idoraforditasok', data);
      return response.data;
    } catch (error: any) {
      console.error('Hiba az időráfordítás létrehozása során:', error);
      throw this.toReadableError(error, 'Hiba történt az időráfordítás létrehozása során.');
    }
  }

  async updateIdoraforditas(id: number, data: Partial<IdoraforditasDto>): Promise<IdoraforditasDto> {
    try {
      const response = await api.put<IdoraforditasDto>(`/idoraforditasok/${id}`, data);
      return response.data;
    } catch (error) {
      console.error('Hiba az időráfordítás frissítése során:', error);
      throw this.toReadableError(error, 'Hiba az időráfordítás frissítése során.');
    }
  }

  async deleteIdoraforditas(id: number): Promise<void> {
    try {
      await api.delete(`/idoraforditasok/${id}`);
    } catch (error) {
      console.error('Hiba az időráfordítás törlése során:', error);
      throw this.toReadableError(error, 'Hiba az időráfordítás törlése során.');
    }
  }

  async getTopWorkersForCurrentMonth(topCount: number = 3): Promise<TopWorkerDto[]> {
    try {
      const response = await api.get<TopWorkerDto[]>(
        `/idoraforditasok/top-workers?topCount=${topCount}`
      );
      return response.data;
    } catch (error) {
      console.error('Hiba a hónap legjobb dolgozóinak lekérdezése során:', error);
      throw this.toReadableError(error, 'Hiba a hónap legjobb dolgozóinak lekérdezése során.');
    }
  }

  async getTopWorkersForCurrentWeek(topCount: number = 3): Promise<TopWorkerDto[]> {
    try {
      const response = await api.get<TopWorkerDto[]>(
        `/idoraforditasok/top-workers-week?topCount=${topCount}`
      );
      return response.data;
    } catch (error) {
      console.error('Hiba a hét legjobb dolgozóinak lekérdezése során:', error);
      throw this.toReadableError(error, 'Hiba a hét legjobb dolgozóinak lekérdezése során.');
    }
  }

  async getTopMunkaszamok(topCount: number = 10): Promise<MunkaszamOsszesitoDto[]> {
    try {
      const response = await api.get<MunkaszamOsszesitoDto[]>(
        `/idoraforditasok/top-munkaszamok?topCount=${topCount}`
      );
      return response.data;
    } catch (error) {
      console.error('Hiba a top munkaszámok lekérdezése során:', error);
      throw this.toReadableError(error, 'Hiba a top munkaszámok lekérdezése során.');
    }
  }

  async getTopActiveMunkaszamok(topCount: number = 10): Promise<MunkaszamOsszesitoDto[]> {
    try {
      const response = await api.get<MunkaszamOsszesitoDto[]>(
        `/idoraforditasok/top-aktiv-munkaszamok?topCount=${topCount}`
      );
      return response.data;
    } catch (error) {
      console.error('Hiba az aktív top munkaszámok lekérdezése során:', error);
      throw this.toReadableError(error, 'Hiba az aktív top munkaszámok lekérdezése során.');
    }
  }
}

export const idoraforditasService = new IdoraforditasService();
